/**
 * Member-search dialog — a REUSABLE contact picker (v2 — Transaction Ledger,
 * DNKADM-0149 U1).
 *
 * A debounced search-as-you-type over `searchContacts` (`GET /v2/admin/members?search=…`,
 * `api/members.ts`, already 0-indexed-correct), a paged result list, and an
 * `onPick(contact: { contactId, name })` callback. It is the shared "who" picker for
 * the Transaction Ledger's member filter (U2) and the member-detail Transactions tab
 * (DNKADM-0150) — so it carries NO screen-specific state; the consumer sets its own
 * chip / filter from the picked `{ contactId, name }`.
 *
 * **Enrichment is best-effort:** a row's `name` may be null — the label degrades to
 * `email`, then the neutral "Unnamed member" placeholder (DNKADM-0338 U2: never a bare
 * contact id rendered as a person). The picked `name` is that same resolved label so the
 * consumer's chip always has text.
 *
 * **Only a real contact id is pickable.** `searchContacts` yields `contactId: null`
 * until the roster surfaces the external id (pre-saas-auth, mirrors AddMemberDialog);
 * such a row is shown disabled with an honest note rather than an error, so `onPick`
 * always receives a non-null `contactId`.
 *
 * **Endpoint tolerance:** `searchContacts` resolves `null` on a `404` (the picker
 * surface not live yet) — represented as an inline "unavailable" note, never a crash.
 *
 * Mounted by the parent only when open (its search resets per open). Bearer-only via
 * `searchContacts` → `request()` (`/v2/admin/**`): no CSRF, no cookies.
 */
import { useCallback, useId, useMemo, useState } from 'react';
import { IconAction } from '@/components/IconAction';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useResource } from '@/hooks/useResource';
import { searchContacts, type ContactSummary } from '@/api/members';
import { Icon, resolveTokens } from '@/theme';
import { personLabel } from '@/utils/personLabel';

/** The picked contact handed back to the consumer — a real (non-null) id + a display label. */
export interface PickedContact {
  contactId: string;
  name: string;
}

/** Debounce window (ms) for the search-as-you-type (matches AddMemberDialog's picker). */
const SEARCH_DEBOUNCE_MS = 250;

/** Results per page for the picker list. */
const PAGE_SIZE = 10;

/** Shown when the contacts surface isn't live yet (a `404`). */
const UNAVAILABLE_MSG =
  'Member search isn’t available yet — the backend for this is still on the way. Please check back soon.';

/** Shown on a row whose external id hasn't synced yet (pre-saas-auth). */
const NOT_SYNCED_MSG = 'Not available until identity sync is enabled';

/** The display label for a contact: `name → email → "Unnamed member"` — never an id. */
function contactLabel(c: ContactSummary): string {
  return personLabel(c.name, c.email);
}

interface MemberSearchDialogProps {
  /** Close the dialog (Cancel / the close button / after a pick). */
  onClose: () => void;
  /** Called with the picked contact (a real id + resolved label); the parent sets its filter. */
  onPick: (contact: PickedContact) => void;
  /** Optional dialog title (defaults to "Find a member"). */
  title?: string;
}

/**
 * The reusable member-search dialog. Debounced search → a paged result list → a
 * `{ contactId, name }` pick. Loading / empty / error / endpoint-unavailable states
 * are all inline; only a real-id row is pickable.
 */
export function MemberSearchDialog({ onClose, onPick, title = 'Find a member' }: MemberSearchDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${useId()}-title`;
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const contacts = useResource(
    useCallback(
      () => searchContacts(debouncedQuery, page, PAGE_SIZE),
      [debouncedQuery, page],
    ),
    [debouncedQuery, page],
  );

  // `searchContacts` resolves `null` on a `404` (the surface isn't live yet).
  const unavailable = contacts.status === 'ready' && contacts.data === null;
  const items = contacts.data?.contacts ?? [];
  const total = contacts.data?.total ?? null;

  // Next-page availability: prefer the total when the server supplies it, else fall
  // back to a full page as the heuristic (a short page is the last page).
  const hasNext = useMemo(() => {
    if (total !== null) return page * PAGE_SIZE < total;
    return items.length === PAGE_SIZE;
  }, [total, page, items.length]);

  function handleQueryChange(value: string) {
    setQuery(value);
    setPage(1); // a new search term restarts at the first page
  }

  function pick(c: ContactSummary) {
    if (c.contactId === null) return; // belt-and-braces — the row is disabled anyway
    onPick({ contactId: c.contactId, name: contactLabel(c) });
    onClose();
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Icon name="person_search" sx={{ fontSize: 22, color: t.brand }} />
        <Box sx={{ flex: 1 }}>{title}</Box>
        <IconAction size="small" onClick={onClose} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <TextField
          label="Search members"
          fullWidth
          size="small"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="Search by name or email"
          helperText="Type to search, then pick a member."
          slotProps={{ htmlInput: { 'aria-label': 'Search members' } }}
          sx={{ mt: 0.5, mb: 1.5 }}
        />

        {unavailable ? (
          <Typography variant="body2" role="note" sx={{ color: 'text.secondary', py: 2 }}>
            {UNAVAILABLE_MSG}
          </Typography>
        ) : contacts.status === 'loading' ? (
          <Typography variant="body2" role="status" sx={{ color: 'text.secondary', py: 2 }}>
            Searching…
          </Typography>
        ) : contacts.status === 'error' ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={contacts.refetch}>
                Retry
              </Button>
            }
          >
            Couldn’t search members.
          </Alert>
        ) : items.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
            No members match this search.
          </Typography>
        ) : (
          <List sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {items.map((c, i) => {
              const label = contactLabel(c);
              const disabled = c.contactId === null;
              // A row with a name shows the email as the secondary line; a name-less
              // row already used the email/id as its label, so no duplicate secondary.
              const secondary = disabled
                ? NOT_SYNCED_MSG
                : c.name !== null
                  ? c.email
                  : null;
              return (
                <ListItemButton
                  key={c.contactId ?? `noid-${i}`}
                  onClick={() => pick(c)}
                  disabled={disabled}
                >
                  <ListItemText primary={label} secondary={secondary} />
                </ListItemButton>
              );
            })}
          </List>
        )}

        {!unavailable && (page > 1 || hasNext) && (
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1 }}
          >
            <Button
              size="small"
              startIcon={<Icon name="chevron_left" />}
              disabled={page <= 1 || contacts.status === 'loading'}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Page {page}
            </Typography>
            <Button
              size="small"
              endIcon={<Icon name="chevron_right" />}
              disabled={!hasNext || contacts.status === 'loading'}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={onClose} sx={{ color: t.text2 }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
